import {
  BlockLayoutType,
  ServerBlockTypeType,
  getStyles
} from "@pathwright/blocks-core"
// Fractional positioning removed – client uses numeric indices only
import { mergeWith } from "lodash"
import { getBlockType } from "../block/utils"
import {
  BlockDataType,
  BlockStyleType,
  BlockType,
  BlocksChanges,
  BlocksContentType,
  CopiedBlockClipType
} from "../types"
import {
  createID,
  handleMissingBlock,
  immutableSplice,
  replaceBlockInState,
  sortBlocks,
  updateBlocksOrders
} from "../utils/utils"
import { EDITOR_MODE } from "./constants"
import { EditorStateType } from "./editorState"

type StatePartial = Partial<EditorStateType>

const QUESTION_BLOCK_TYPE = "question"
const QUOTE_BLOCK_TYPE = "quote"
const MATCHING_LAYOUT = "matching"
const QUESTION_ARRAY_KEYS = ["options", "matches"]

const createBlankQuestionOption = (order: number) => ({
  label: "",
  order,
  value: createID()
})

const mergeLayoutData = ({
  block,
  layout,
  layoutData
}: {
  block: BlockType
  layout: string
  layoutData: BlockDataType
}): BlockDataType => {
  // Legacy content uses lowercase types; match getBlockType's lookup behavior.
  const isQuestion = block.type.toLowerCase() === QUESTION_BLOCK_TYPE
  const nextData = mergeWith(
    {}, // Start with default object
    layoutData, // Add new layout data (includes example data)
    block.data, // Overlay existing data (so we don't lose any authored data)
    { layout }, // Add the new layout
    // This callback handles an exception to the default merge logic:
    // Question options and matches should be completely replaced when change layouts.
    (_obj, src, key) => {
      if (
        isQuestion &&
        QUESTION_ARRAY_KEYS.includes(`${key}`) &&
        Array.isArray(src)
      ) {
        return src
      }
    }
  )

  // A layout change must not attach a sample portrait or author to an existing quote.
  if (block.type.toLowerCase() === QUOTE_BLOCK_TYPE) {
    return {
      ...nextData,
      image: block.data?.image ?? "",
      attribution: block.data?.attribution ?? ""
    }
  }

  // If we're switching to the Question block's matching layout,
  // we need to preserve existing matches where they exist,
  // and create new (blank)matches for any new options.
  if (isQuestion && layout === MATCHING_LAYOUT) {
    const options = Array.isArray(nextData.options) ? nextData.options : []
    const matches = Array.isArray(block.data?.matches) ? block.data.matches : []

    return {
      ...nextData,
      matches: options.map((option: any, index: number) => ({
        ...createBlankQuestionOption(index + 1),
        ...matches[index],
        order: option.order || index + 1
      }))
    }
  }

  return nextData
}

export const startSyncingAction = () => (): StatePartial => {
  return {
    syncing: true
  }
}

export const stopSyncingAction = () => (): StatePartial => {
  return {
    syncing: false
  }
}

export const addBlockRequestAction =
  ({ type, order, data, layout, style }: any) =>
  (state: EditorStateType): StatePartial => {
    const blocksLength = state.blocks.length
    const index =
      order === undefined
        ? blocksLength
        : Math.max(0, Math.min(blocksLength, (order as number) - 1))
    return {
      addingBlockIndex: index,
      syncing: true
    }
  }

export const addBlockErrorAction = () => (): StatePartial => {
  return {
    addingBlockIndex: undefined,
    syncing: false
  }
}

export const errorAction =
  ({ error }: { error: Error }) =>
  (): StatePartial => {
    return {
      error,
      publishing: false,
      discarding: false,
      syncing: false
    }
  }

export const addBlockAction =
  ({
    type,
    order,
    data,
    layout,
    style,
    id
  }: {
    type: string
    order?: number // is the index + 1
    data: BlockDataType
    layout: any
    style?: BlockStyleType
    id: string
  }) =>
  (state: EditorStateType): StatePartial => {
    // 1) Work with a copy of the blocks array
    let blocks = [...state.blocks]

    // 2) Compute the zero-based index where we want to insert.
    //    If order is missing, append to the end.
    const newIndex =
      order === undefined ? blocks.length : ((order - 1) as number)

    // 3) Derive the numeric order: index is zero-based, order is one-based
    const newOrder = newIndex + 1

    // 4) Create a new block using that fractional string in `order`.
    const newBlock = {
      type,
      order: newOrder as number,
      id: id || `new-${createID()}`,
      contentID: state.contentID,
      data,
      layout,
      style
    } as BlockType

    // 5) Insert it into our local array at newIndex

    blocks.splice(newIndex, 0, newBlock)
    blocks = updateBlocksOrders(blocks)

    // 6) Return the new state with blocks re‐sorted by numeric order
    return {
      blocks: sortBlocks(blocks),
      lastModified: new Date().getTime()
    }
  }

export const clearDeleteBlockAction = () => (): StatePartial => {
  return {
    deleteBlockID: undefined
  }
}

export const deleteBlockAction =
  ({ id }: { id: string }) =>
  (state: EditorStateType): StatePartial => {
    const block = state.blocks.find((b) => b.id === id)

    // Nothing left to do if block doesn't exist
    if (!block) return handleMissingBlock(id, "delete")

    // set the deleteBlockID if it's not already set
    if (id !== state.deleteBlockID) {
      return {
        deleteBlockID: id
      }
    }

    // remove the block and reset numeric orders sequentially
    let blocks = [...state.blocks].filter((b) => b.id !== id)
    blocks = updateBlocksOrders(blocks)

    const stateChange: StatePartial = {
      blocks: sortBlocks(blocks),
      lastModified: new Date().getTime(),
      deleteBlockID: undefined
    }

    // Clear the clipboard if it's the block we're deleting
    if (state.copiedBlockClip && state.copiedBlockClip.object_id.includes(id)) {
      stateChange.copiedBlockClip = undefined
      stateChange.copiedBlock = undefined
    }

    return stateChange
  }

export const updateBlockAction =
  ({ id, block: blockUpdate }: { id: string; block: Partial<BlockType> }) =>
  (state: EditorStateType): StatePartial => {
    const oldBlock = state.blocks.find((b) => b.id === id)

    if (!oldBlock) return handleMissingBlock(id)

    const nextBlock = {
      ...oldBlock,
      ...blockUpdate
    }

    return {
      blocks: replaceBlockInState(nextBlock, state),
      lastModified: new Date().getTime()
    }
  }

export const updateBlockLayoutAction =
  ({
    id,
    layout,
    blockTypes
  }: {
    id: string
    layout: string
    blockTypes: ServerBlockTypeType[]
  }) =>
  (state: EditorStateType): StatePartial => {
    const block: BlockType | undefined = state.blocks.find(
      (b: BlockType) => b.id === id
    )

    if (!block) return handleMissingBlock(id)

    // no need to update the layout if it's the same
    if (layout === block.layout) return {}

    // if the layout of a block is changed,
    // we need to merge in the default layout data
    const blockType: ServerBlockTypeType | undefined = getBlockType(
      block.type,
      blockTypes
    )
    if (!blockType) return {}

    const { layouts } = blockType
    const nextLayout: BlockLayoutType | undefined = Object.values(layouts).find(
      (l) => l.key === layout
    )
    if (!nextLayout) return {}

    const nextData = mergeLayoutData({
      block,
      layout,
      layoutData: nextLayout.data
    })

    const nextBlock = {
      ...block,
      layout,
      data: nextData
    }

    return {
      blocks: replaceBlockInState(nextBlock, state),
      lastModified: new Date().getTime()
    }
  }

export const updateBlockStyleAction =
  ({ id, style }: { id: string; style: BlockStyleType }) =>
  (state: EditorStateType): StatePartial => {
    const block: BlockType | undefined = state.blocks.find(
      (block) => block.id === id
    )

    if (!block) return handleMissingBlock(id)

    const nextStyle = getStyles(block, style)

    const nextBlock = {
      ...block,
      style: nextStyle
    }

    return {
      blocks: replaceBlockInState(nextBlock, state),
      lastModified: new Date().getTime()
    }
  }

export const setCopiedBlockAction =
  ({ copiedBlockClip }: { copiedBlockClip: CopiedBlockClipType | undefined }) =>
  (): StatePartial => {
    return {
      copiedBlockClip,
      pasteError: undefined
    }
  }

export const clearCopiedBlockAction = () => (): StatePartial => {
  return {
    copiedBlockClip: undefined,
    copiedBlock: undefined,
    pasteError: undefined
  }
}

export const pasteBlockAction =
  ({ index }: { index: number }) =>
  (): StatePartial => {
    return {
      syncing: true,
      pastingBlockIndex: index,
      pasteError: undefined
    }
  }

export const pasteBlockErrorAction =
  ({ error }: { error?: string } = {}) =>
  (): StatePartial => {
    return {
      syncing: false,
      pastingBlockIndex: undefined,
      pasteError: error
    }
  }

export const syncedPasteBlockAction =
  ({ copiedBlock }: { copiedBlock: BlockType }) =>
  (state: EditorStateType): StatePartial => {
    const order =
      state.pastingBlockIndex !== undefined
        ? state.pastingBlockIndex + 1
        : copiedBlock.order || state.blocks.length + 1

    const nextState = {
      ...addBlockAction({
        ...copiedBlock,
        order
      })(state),
      copiedBlockClip: undefined,
      pastingBlockIndex: undefined,
      pasteError: undefined
    }

    return {
      ...nextState,
      syncing: false,
      changes: copiedBlock.contentChanges
    }
  }

export const syncedPasteBlocksAction =
  ({ copiedBlocks }: { copiedBlocks: BlockType[] }) =>
  (state: EditorStateType): StatePartial => {
    const startingOrder =
      state.pastingBlockIndex !== undefined
        ? state.pastingBlockIndex + 1
        : state.blocks.length + 1

    let blocks = [...state.blocks]
    const insertionIndex = startingOrder - 1

    // Prepare new blocks with correct initial orders
    const newBlocks = copiedBlocks.map((block, index) => ({
      ...block,
      order: startingOrder + index
    }))

    // Insert all new blocks at once at the correct position
    blocks.splice(insertionIndex, 0, ...newBlocks)

    // Update all block orders and sort
    blocks = updateBlocksOrders(blocks)

    return {
      blocks: sortBlocks(blocks),
      copiedBlockClip: undefined,
      pastingBlockIndex: undefined,
      pasteError: undefined,
      syncing: false,
      lastModified: new Date().getTime()
    }
  }

export const syncedAddBlockAction =
  (newBlock: BlockType) =>
  (state: EditorStateType): StatePartial => {
    // New flow: no optimistic block exists. Insert confirmed block now.
    let blocks = [...state.blocks]
    const preferredIndex =
      typeof state.addingBlockIndex === "number"
        ? state.addingBlockIndex
        : newBlock.order
        ? Math.max(0, Math.min(blocks.length, (newBlock.order as number) - 1))
        : blocks.length
    blocks.splice(preferredIndex, 0, newBlock)
    blocks = updateBlocksOrders(blocks)

    return {
      blocks: sortBlocks(blocks),
      syncing: false,
      addingBlockIndex: undefined,
      changes: newBlock.contentChanges,
      lastModified: new Date().getTime()
    }
  }

export const syncedUpdateBlockAction =
  (block: BlockType) =>
  (state: EditorStateType): StatePartial => {
    const oldBlock: BlockType | undefined = state.blocks.find(
      (b) => b.id === block.id
    )

    if (!oldBlock) {
      return handleMissingBlock(block.id, "update", {
        syncing: false,
        changes: block.contentChanges
      })
    }

    const mergedBlock = {
      ...oldBlock,
      ...block
    }

    return {
      blocks: replaceBlockInState(mergedBlock, state),
      syncing: false,
      changes: block.contentChanges
    }
  }

export const syncedBlocksAction =
  (content: Partial<BlocksContentType>) => (): StatePartial => {
    return {
      syncing: false,
      changes: content.changes
    }
  }

const moveBlock = (
  state: EditorStateType,
  id: string,
  oldIndex: number,
  newIndex: number
): StatePartial => {
  if (oldIndex === newIndex) return {}

  const block: BlockType | undefined = state.blocks.find((b) => b.id === id)

  if (!block) return handleMissingBlock(id, "move")

  const blocks = [...state.blocks].filter((b) => b.id !== block.id)
  const order = newIndex + 1

  const sliced: BlockType[] = immutableSplice(blocks, newIndex, 0, {
    ...block,
    order
  })

  return {
    blocks: updateBlocksOrders(sliced),
    lastModified: new Date().getTime()
  }
}

export const moveBlockUpAction =
  ({ oldIndex, id }: { oldIndex: number; id: string }) =>
  (state: EditorStateType): StatePartial => {
    const newIndex = Math.max(0, oldIndex - 1)

    return moveBlock(state, id, oldIndex, newIndex)
  }

export const moveBlockDownAction =
  ({ oldIndex, id }: { oldIndex: number; id: string }) =>
  (state: EditorStateType): StatePartial => {
    const newIndex = Math.min(state.blocks.length - 1, oldIndex + 1)

    return moveBlock(state, id, oldIndex, newIndex)
  }

export const setModeAction =
  () =>
  (state: EditorStateType): StatePartial => {
    const mode =
      state.mode === EDITOR_MODE.EDIT ? EDITOR_MODE.VIEW : EDITOR_MODE.EDIT

    return {
      mode
    }
  }

export const startPublishAction = () => (): StatePartial => {
  return {
    publishing: true
  }
}

export const syncedPublishAction =
  ({ lastPublishedDateTime, lastModifiedDateTime, changes }) =>
  (): StatePartial => {
    return {
      lastPublished: lastPublishedDateTime,
      lastModified: lastModifiedDateTime,
      publishing: false,
      syncing: false,
      changes
    }
  }

export const startDiscardDraftAction = () => (): StatePartial => {
  return {
    discarding: true
  }
}

export const syncedDiscardDraftAction =
  ({ lastPublishedDateTime, lastModifiedDateTime, blocks, changes }) =>
  (): StatePartial => {
    return {
      lastPublished: lastPublishedDateTime,
      lastModified: lastModifiedDateTime,
      blocks,
      discarding: false,
      syncing: false,
      changes
    }
  }

export const setChangesAction =
  ({ changes }: { changes?: BlocksChanges }) =>
  (): StatePartial => {
    return {
      changes
    }
  }
